// frontend/packages/business-console/src/components/primitives/StatusPill.jsx
//
// The one semantic status pill for the whole console. Color encodes
// meaning (handoff README section 2): green = paid/published/positive,
// amber = pending/processing/warning, red = error/overdue, gray =
// draft/neutral, blue = primary/info, purple = AI. Every list table,
// header, and detail view renders state through this so pills never
// drift between pages.
//
// Domain pages map their own state enum to a `kind` + `label` and pass
// it in (e.g. payment "succeeded" -> kind "success", label "Paid"); the
// visual treatment lives here, once.

import React from 'react';
import { Box } from '@mui/material';

import { useTokens } from './useTokens';

// kind -> token roles. `bg`/`border`/`text` are token keys resolved
// against the active theme; `dot` is the leading-dot color key.
const KIND_ROLES = {
  success: {
    text: 'greenText',
    bg: 'greenBg',
    border: 'greenBorder',
    dot: 'green',
  },
  warning: {
    text: 'amberText',
    bg: 'amberBg',
    border: 'amberBorder',
    dot: 'amber',
  },
  error: { text: 'red', bg: 'grayBg', border: 'grayBorder', dot: 'red' },
  neutral: {
    text: 'grayText',
    bg: 'grayBg',
    border: 'grayBorder',
    dot: 'faint',
  },
  info: { text: 'blue', bg: 'blueBg', border: 'blueBorder', dot: 'blue' },
  ai: { text: 'purple', bg: 'purpleBg', border: 'purpleBorder', dot: 'purple' },
};

const SIZES = {
  sm: { fontSize: 10, px: '7px', py: '2px', radius: '5px', dot: 6 },
  md: { fontSize: 11, px: '9px', py: '3px', radius: '6px', dot: 7 },
};

export default function StatusPill({
  kind = 'neutral',
  label,
  dot = false,
  size = 'md',
  sx,
}) {
  const t = useTokens();
  const role = KIND_ROLES[kind] || KIND_ROLES.neutral;
  const s = SIZES[size] || SIZES.md;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: s.fontSize,
        fontWeight: 800,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        color: t[role.text],
        bgcolor: t[role.bg],
        border: `1px solid ${t[role.border]}`,
        px: s.px,
        py: s.py,
        borderRadius: s.radius,
        ...sx,
      }}
    >
      {dot ? (
        <Box
          component="span"
          sx={{
            width: s.dot,
            height: s.dot,
            borderRadius: '50%',
            bgcolor: t[role.dot],
            flexShrink: 0,
          }}
        />
      ) : null}
      {label}
    </Box>
  );
}
